// src/components/MapComponent.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import zunoLogo from "../assets/zuno-splash.png";
import { useTheme } from "../theme/ThemeContext";
import { isStoreScreenshotCapture } from "../screenshotSession";
import { Capacitor } from "@capacitor/core";
import { getCurrentLocation } from "../services/locationService";
import { markPerformance } from "../services/performanceTrace";
import MapDiagnosticPanel from "./MapDiagnosticPanel";
import { classifyMapFailure, recordMapDiagnostic } from "../services/mapDiagnostics";

const StoreScreenshotMap = React.lazy(() => import("./StoreScreenshotMap"));
const NativeIOSMap = React.lazy(() => import("./NativeIOSMap"));

// Cache de rotas com TTL de 30 minutos e limite de 20 entradas
const _rotaCache = new Map();
const _ROTA_CACHE_TTL = 30 * 60 * 1000; // 30 min em ms
function _rotaCacheGet(key) {
  const entry = _rotaCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > _ROTA_CACHE_TTL) { _rotaCache.delete(key); return null; }
  return entry.data;
}
function _rotaCacheSet(key, data) {
  if (_rotaCache.size >= 20) _rotaCache.delete(_rotaCache.keys().next().value);
  _rotaCache.set(key, { data, ts: Date.now() });
}

// ── Marcador motorista (roxo — Z Zuno) ──
const DRIVER_MARKER_SVG = `
<svg width="48" height="64" viewBox="0 0 48 64" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="drvGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:#7c3aed"/>
      <stop offset="100%" style="stop-color:#4c1d95"/>
    </linearGradient>
    <filter id="drvShadow" x="-30%" y="-20%" width="160%" height="160%">
      <feDropShadow dx="0" dy="3" stdDeviation="3" flood-color="rgba(124,58,237,0.55)"/>
    </filter>
  </defs>
  <path d="M24 0C10.745 0 0 10.745 0 24c0 16 24 40 24 40S48 40 48 24C48 10.745 37.255 0 24 0z"
        fill="url(#drvGrad)" filter="url(#drvShadow)"/>
  <circle cx="24" cy="23" r="15" fill="white" opacity="0.95"/>
  <text x="24" y="29" text-anchor="middle" font-family="Arial Black, Arial, sans-serif"
        font-size="17" font-weight="900" fill="#7c3aed">Z</text>
  <ellipse cx="24" cy="64" rx="7" ry="2" fill="rgba(0,0,0,0.2)"/>
</svg>
`;

// ── Marcador passageiro (ciano/azul) ──
const PASSENGER_MARKER_SVG = `
<svg width="52" height="68" viewBox="0 0 52 68" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="passGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:#06b6d4"/>
      <stop offset="100%" style="stop-color:#3b82f6"/>
    </linearGradient>
    <filter id="passShadow" x="-30%" y="-20%" width="160%" height="160%">
      <feDropShadow dx="0" dy="3" stdDeviation="3" flood-color="rgba(6,182,212,0.55)"/>
    </filter>
  </defs>
  <path d="M26 0C11.641 0 0 11.641 0 26c0 17.333 26 42 26 42S52 43.333 52 26C52 11.641 40.359 0 26 0z"
        fill="url(#passGrad)" filter="url(#passShadow)"/>
  <circle cx="26" cy="25" r="16" fill="white" opacity="0.95"/>
  <text x="26" y="31" text-anchor="middle" font-family="Arial Black, Arial, sans-serif"
        font-size="18" font-weight="900" fill="#06b6d4">Z</text>
  <ellipse cx="26" cy="68" rx="8" ry="2.5" fill="rgba(0,0,0,0.2)"/>
</svg>
`;

const CAR_MARKER_SVG = `
<svg width="52" height="52" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
  <circle cx="26" cy="26" r="24" fill="#1a1a2e" stroke="#fff" stroke-width="2.5"/>
  <g transform="translate(13,11) scale(0.5)">
    <path d="M4 30 L4 22 Q4 18 8 18 L14 18 Q19 6 30 4 L42 4 Q54 4 58 18 L64 18 Q68 18 68 22 L68 30 Q68 34 64 34 L8 34 Q4 34 4 30 Z" fill="#e2e8f0"/>
    <path d="M18 18 Q22 7 30 5 L42 5 Q52 6 56 18 Z" fill="#bfdbfe" opacity="0.9"/>
    <rect x="62" y="20" width="6" height="4" rx="2" fill="#fde68a"/>
    <rect x="4" y="21" width="5" height="4" rx="1.5" fill="#fca5a5"/>
    <circle cx="18" cy="34" r="7" fill="#1e293b"/><circle cx="18" cy="34" r="4" fill="#475569"/>
    <circle cx="54" cy="34" r="7" fill="#1e293b"/><circle cx="54" cy="34" r="4" fill="#475569"/>
  </g>
</svg>
`;

export const isGoogleMapsRuntimeReady = () => typeof window.google?.maps?.Map === "function";
export const shouldUseNativeIOSMap = (platform = Capacitor.getPlatform()) => platform === "ios";

export default function MapComponent(props) {
  if (shouldUseNativeIOSMap()) {
    return <React.Suspense fallback={<div className="zuno-map-loading" style={{ width: "100%", height: "100%" }} />}><NativeIOSMap {...props} mapStyles={getZunoMapStyles} /></React.Suspense>;
  }
  return isStoreScreenshotCapture
    ? <React.Suspense fallback={<div className="zuno-map-loading" style={{ width: "100%", height: "100%", background: "#dbe7ef" }} />}><StoreScreenshotMap {...props} /></React.Suspense>
    : <GoogleMapComponent {...props} />;
}

function GoogleMapComponent({ destino, onRouteCalculated, corridaConfirmada, userLocation, hasPermission, onRequestLocation, style, motoristaPosicao, corridaStatus, showControls = true, forceDark = false }) {
  const { theme } = useTheme();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const userMarkerRef = useRef(null);
  const destinoMarkerRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const driverRouteRendererRef = useRef(null);
  const driverRouteAlvoRef = useRef(null); // evita recalcular mesma rota
  const positionRef = useRef(null);
  const ultimoDestinoRotaRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const mapReadyRef = useRef(false);
  const [initialized, setInitialized] = useState(false);
  const [googleMapsReady, setGoogleMapsReady] = useState(isGoogleMapsRuntimeReady);
  const [mapError, setMapError] = useState(null);
  const mapStartedAtRef = useRef(performance.now());

  const logMap = useCallback((stage, extra = {}) => {
    console.info("[ZunoMap]", {
      stage,
      elapsedMs: Math.round(performance.now() - mapStartedAtRef.current),
      origin: window.location.origin,
      platform: Capacitor.getPlatform(),
      ...extra,
    });
  }, []);

  const polylineBaseRef = useRef(null);
  const polylineFluxoRef = useRef(null);
  const animFrameRef = useRef(null);
  const offsetRef = useRef(0);

  const pararAnimacao = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    if (polylineBaseRef.current) { polylineBaseRef.current.setMap(null); polylineBaseRef.current = null; }
    if (polylineFluxoRef.current) { polylineFluxoRef.current.setMap(null); polylineFluxoRef.current = null; }
  }, []);

  const animarFluxo = useCallback(() => {
    if (!polylineFluxoRef.current) return;
    offsetRef.current = (offsetRef.current + 0.02) % (Math.PI * 2);
    const opacidade = 0.3 + 0.7 * ((Math.sin(offsetRef.current) + 1) / 2);
    polylineFluxoRef.current.setOptions({ strokeOpacity: opacidade });
    animFrameRef.current = requestAnimationFrame(animarFluxo);
  }, []);

  const desenharRotaAnimada = useCallback((pontos) => {
    const map = mapInstanceRef.current;
    if (!map || !window.google || !pontos?.length) return;
    pararAnimacao();
    polylineBaseRef.current = new window.google.maps.Polyline({
      path: pontos, map,
      strokeColor: "#D82DCC", strokeWeight: 9, strokeOpacity: 0.42, zIndex: 1,
    });
    polylineFluxoRef.current = new window.google.maps.Polyline({
      path: pontos, map,
      strokeColor: "#7657FF", strokeWeight: 5, strokeOpacity: 1, zIndex: 2,
    });
    offsetRef.current = 0;
    cancelAnimationFrame(animFrameRef.current);
    animarFluxo();
  }, [pararAnimacao, animarFluxo]);

  const centerUserInVisibleMap = useCallback((position, animate = false) => {
    const map = mapInstanceRef.current;
    const mapElement = mapRef.current;
    if (!map || !mapElement || !window.google?.maps || !position) return;

    const applyOffset = () => {
      const projection = map.getProjection();
      const zoom = map.getZoom();
      if (!projection || zoom == null) return;

      const point = projection.fromLatLngToPoint(new window.google.maps.LatLng(position));
      if (!point) return;

      const sheetTop = document.querySelector(".passenger-home-screen .zuno-home-sheet, .passenger-home-screen .zuno-search-sheet, .passenger-home-screen .zuno-driver-sheet")?.getBoundingClientRect().top;
      const visibleMapHeight = sheetTop && sheetTop > 0 ? sheetTop : mapElement.clientHeight * 0.5;
      const desiredMarkerY = visibleMapHeight * 0.5;
      const pixelOffset = Math.max(0, mapElement.clientHeight * 0.5 - desiredMarkerY);
      const scale = 2 ** zoom;
      const centerPoint = new window.google.maps.Point(point.x, point.y + pixelOffset / scale);
      const adjustedCenter = projection.fromPointToLatLng(centerPoint);

      if (adjustedCenter) {
        if (animate) map.panTo(adjustedCenter);
        else map.setCenter(adjustedCenter);
      }
    };

    if (map.getProjection()) applyOffset();
    else window.google.maps.event.addListenerOnce(map, "projection_changed", applyOffset);
  }, []);

  // O loader global pode concluir depois que a Home já foi montada. Aguarda a API
  // explicitamente para não deixar o contêiner branco por uma condição de corrida.
  useEffect(() => {
    const latitude = Number(userLocation?.lat);
    const longitude = Number(userLocation?.lng);
    const locationValid = Number.isFinite(latitude) && Number.isFinite(longitude)
      && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180
      && !(latitude === 0 && longitude === 0);
    recordMapDiagnostic(locationValid ? "LOCATION_VALID" : "LOCATION_INVALID", {
      latitude: Number.isFinite(latitude) ? latitude : null,
      longitude: Number.isFinite(longitude) ? longitude : null,
      locationValid,
      failureType: locationValid ? null : "LOCATION_FAILURE",
    });
  }, [userLocation]);

  useEffect(() => {
    logMap("MAP_INIT_START", { hasPermission: Boolean(hasPermission), hasUserLocation: Boolean(userLocation) });
    markPerformance("MAP_INIT_START", { hasPermission: Boolean(hasPermission), hasUserLocation: Boolean(userLocation) }, { onlyOnce: true });
    const onMapError = (event) => setMapError(event.detail || { code: "maps/unknown" });
    window.addEventListener("zuno:map-error", onMapError);
    const timeoutId = window.setTimeout(() => {
      if (mapReadyRef.current) return;
      const detail = { code: "maps/load-timeout", message: "O mapa não respondeu dentro do limite." };
      logMap("MAP_LOAD_TIMEOUT", detail);
      recordMapDiagnostic("MAP_UNKNOWN_FAILURE", {
        failureType: classifyMapFailure(),
        lastError: { name: "MapTimeout", message: detail.message, stack: null, type: "timeout" },
      });
      setMapError(detail);
    }, 12000);
    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("zuno:map-error", onMapError);
    };
  }, [hasPermission, userLocation, logMap]);

  useEffect(() => {
    if (isGoogleMapsRuntimeReady()) {
      logMap("MAP_SCRIPT_LOADED");
      recordMapDiagnostic("GOOGLE_MAPS_PRESENT");
      setGoogleMapsReady(true);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (!isGoogleMapsRuntimeReady()) return;
      window.clearInterval(intervalId);
      logMap("MAP_SCRIPT_LOADED");
      recordMapDiagnostic("GOOGLE_MAPS_PRESENT");
      setGoogleMapsReady(true);
    }, 150);

    return () => window.clearInterval(intervalId);
  }, [logMap]);

  // Inicializa mapa
  useEffect(() => {
    if (!googleMapsReady || !isGoogleMapsRuntimeReady() || mapInstanceRef.current || initialized) return;
    const center = (hasPermission && userLocation) ? userLocation : { lat: -14.235, lng: -51.925 };
    const zoom = (hasPermission && userLocation) ? 15 : 5;
    const tryInit = () => initMap(center, zoom);
    if (tryInit()) return undefined;
    const observer = window.ResizeObserver ? new window.ResizeObserver(tryInit) : null;
    if (mapRef.current && observer) observer.observe(mapRef.current);
    const frame = window.requestAnimationFrame(tryInit);
    return () => { window.cancelAnimationFrame(frame); observer?.disconnect(); };
  }, [googleMapsReady, initialized, hasPermission, userLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Atualiza marcador do usuário
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return;
    if (hasPermission && userLocation) {
      if (!destino) {
        mapInstanceRef.current.setZoom(17);
        centerUserInVisibleMap(userLocation);
      }
      positionRef.current = userLocation;
      if (!userMarkerRef.current) {
        userMarkerRef.current = new window.google.maps.Marker({
          position: userLocation,
          map: mapInstanceRef.current,
          title: "Você",
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(PASSENGER_MARKER_SVG)}`,
            scaledSize: new window.google.maps.Size(52, 68),
            anchor: new window.google.maps.Point(26, 60),
          },
          zIndex: 10,
        });
      } else {
        userMarkerRef.current.setPosition(userLocation);
      }
    }
  }, [mapReady, hasPermission, userLocation, destino, centerUserInVisibleMap]);

  // Recalibra a câmera quando o painel ou a viewport mudam de tamanho.
  useEffect(() => {
    if (!mapReady || destino || !positionRef.current) return undefined;
    const recenter = () => centerUserInVisibleMap(positionRef.current);
    const sheet = document.querySelector(".passenger-home-screen .zuno-home-sheet, .passenger-home-screen .zuno-search-sheet, .passenger-home-screen .zuno-driver-sheet");
    const observer = window.ResizeObserver ? new window.ResizeObserver(recenter) : null;
    if (sheet && observer) observer.observe(sheet);
    window.addEventListener("resize", recenter);
    const frame = window.requestAnimationFrame(recenter);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", recenter);
      observer?.disconnect();
    };
  }, [mapReady, destino, userLocation, hasPermission, centerUserInVisibleMap]);

  const initMap = (center, zoom = 14) => {
    if (!mapRef.current || mapInstanceRef.current) return Boolean(mapInstanceRef.current);
    const dimensions = { width: mapRef.current.clientWidth, height: mapRef.current.clientHeight };
    recordMapDiagnostic("MAP_CONTAINER_MEASURED", {
      containerWidth: dimensions.width,
      containerHeight: dimensions.height,
    });
    if (!dimensions.width || !dimensions.height) {
      logMap("MAP_CONTAINER_NOT_READY", dimensions);
      recordMapDiagnostic("CONTAINER_FAILURE", { failureType: "CONTAINER_FAILURE" });
      return false;
    }
    try {
    recordMapDiagnostic("MAP_CONSTRUCTOR_STARTED");
    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      rotateControl: false,
      scaleControl: false,
      zoomControl: false,
      draggable: true,
      scrollwheel: true,
      disableDoubleClickZoom: false,
      keyboardShortcuts: true,
      gestureHandling: "greedy",
      styles: getZunoMapStyles(forceDark ? "dark" : theme),
    });
    mapInstanceRef.current = map;
    recordMapDiagnostic("MAP_CONSTRUCTOR_SUCCESS", { mapCreated: true, failureType: null, lastError: null });
    setInitialized(true);
    logMap("MAP_INSTANCE_CREATED", dimensions);

    window.google.maps.event.addListenerOnce(map, "tilesloaded", () => {
      logMap("MAP_TILES_LOADED");
      const container = mapRef.current;
      if (!container) return;
      const sheetTop = document.querySelector(".passenger-home-screen .zuno-home-sheet, .passenger-home-screen .zuno-search-sheet, .passenger-home-screen .zuno-driver-sheet")?.getBoundingClientRect().top;
      const mapBottom = container.getBoundingClientRect().bottom;
      const attributionOffset = window.innerWidth < 900 && sheetTop
        ? `${Math.max(12, Math.round(mapBottom - sheetTop + 8))}px`
        : "12px";
      const mover = (sel) => container.querySelectorAll(sel).forEach(el => {
        el.style.setProperty("margin-bottom", attributionOffset, "important");
        el.style.setProperty("position", "relative", "important");
      });
      mover(".gm-style-cc");
      mover("a[href*='maps.google.com']");
      mover("a[href*='google.com/maps']");
      const wrappers = container.querySelectorAll(".gm-style > div:last-child");
      wrappers.forEach(el => {
        if (el.querySelector("a[href*='google']") || el.querySelector(".gm-style-cc")) {
          el.style.setProperty("margin-bottom", attributionOffset, "important");
        }
      });
      setMapError(null);
      mapReadyRef.current = true;
      setMapReady(true);
      logMap("MAP_READY");
      recordMapDiagnostic("MAP_READY", { mapCreated: true, failureType: null, lastError: null });
      markPerformance("MAP_READY", {}, { onlyOnce: true });
    });
    return true;
    } catch (cause) {
      const detail = { code: cause?.code || "maps/init", message: cause?.message || "Map initialization failed" };
      console.error("[ZunoMap]", { stage: "MAP_INSTANCE_ERROR", ...detail });
      recordMapDiagnostic("MAP_CONSTRUCTOR_ERROR", {
        mapCreated: false,
        failureType: "MAP_CONSTRUCTOR_FAILURE",
        error: cause,
      });
      setMapError(detail);
      return false;
    }
  };

  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return;
    mapInstanceRef.current.setOptions({ styles: getZunoMapStyles(forceDark ? "dark" : theme) });
  }, [forceDark, mapReady, theme]);

  const getMapPadding = useCallback(() => {
    const mapRect = mapRef.current?.getBoundingClientRect();
    if (!mapRect) return { top: 80, right: 40, bottom: 80, left: 40 };
    const sheets = [...document.querySelectorAll(".passenger-home-screen .zuno-home-sheet, .passenger-home-screen .zuno-search-sheet, .passenger-home-screen .zuno-driver-sheet")];
    const activeSheet = sheets
      .map((element) => element.getBoundingClientRect())
      .find((rect) => rect.width > 0 && rect.height > 0);
    return calculateMapPadding(mapRect, activeSheet, window.innerWidth >= 900);
  }, []);

  const fitBoundsComPainel = useCallback((bounds) => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.fitBounds(bounds, getMapPadding());
  }, [getMapPadding]);

  // Destino — marcador e rota
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return;
    if (destinoMarkerRef.current) { destinoMarkerRef.current.setMap(null); destinoMarkerRef.current = null; }
    if (!destino) {
      pararAnimacao();
      if (positionRef.current) {
        mapInstanceRef.current.setCenter(positionRef.current);
        mapInstanceRef.current.setZoom(hasPermission ? 17 : 14);
      }
      ultimoDestinoRotaRef.current = null;
      return;
    }

    const destinoPos = { lat: destino.lat, lng: destino.lng };
    destinoMarkerRef.current = new window.google.maps.Marker({
      position: destinoPos,
      map: mapInstanceRef.current,
      title: destino.name || "Destino",
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(DRIVER_MARKER_SVG)}`,
        scaledSize: new window.google.maps.Size(40, 53),
        anchor: new window.google.maps.Point(20, 53),
      },
      zIndex: 20,
    });

    const destinoKey = `${destino.lat},${destino.lng}`;
    if (ultimoDestinoRotaRef.current === destinoKey) return;

    const cached = _rotaCacheGet(destinoKey);
    if (cached) {
      desenharRotaAnimada(cached.pontos);
      onRouteCalculated?.(cached.km, cached.min);
      ultimoDestinoRotaRef.current = destinoKey;
      if (positionRef.current) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(positionRef.current);
        bounds.extend(destinoPos);
        fitBoundsComPainel(bounds);
      }
      return;
    }

    if (!positionRef.current || !window.google) return;
    const svc = new window.google.maps.DirectionsService();
    svc.route({
      origin: positionRef.current,
      destination: destinoPos,
      travelMode: window.google.maps.TravelMode.DRIVING,
    }, (result, status) => {
      if (status === "OK" && result.routes?.[0]?.legs?.[0]) {
        const leg = result.routes[0].legs[0];
        const pontos = result.routes[0].overview_path || leg.steps.flatMap(s => s.path || []);
        const km = +(leg.distance.value / 1000).toFixed(1);
        const min = Math.ceil(leg.duration.value / 60);
        onRouteCalculated?.(km, min);
        desenharRotaAnimada(pontos);
        _rotaCacheSet(destinoKey, { pontos, km, min });
        ultimoDestinoRotaRef.current = destinoKey;
        const bounds = new window.google.maps.LatLngBounds();
        leg.steps.forEach(step => { bounds.extend(step.start_location); bounds.extend(step.end_location); });
        fitBoundsComPainel(bounds);
      }
    });
  }, [destino, corridaConfirmada, mapReady, onRouteCalculated, hasPermission, fitBoundsComPainel, pararAnimacao, desenharRotaAnimada]);

  useEffect(() => { return () => { cancelAnimationFrame(animFrameRef.current); }; }, []);

  // Marcador do carro do motorista em tempo real
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || !window.google) return;
    if (!motoristaPosicao?.lat || !motoristaPosicao?.lng) {
      if (driverMarkerRef.current) { driverMarkerRef.current.setMap(null); driverMarkerRef.current = null; }
      return;
    }
    const pos = { lat: motoristaPosicao.lat, lng: motoristaPosicao.lng };
    const icon = {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(CAR_MARKER_SVG)}`,
      scaledSize: new window.google.maps.Size(52, 52),
      anchor: new window.google.maps.Point(26, 26),
    };
    if (!driverMarkerRef.current) {
      driverMarkerRef.current = new window.google.maps.Marker({
        position: pos, map: mapInstanceRef.current,
        title: "Motorista", icon, zIndex: 15,
      });
    } else {
      driverMarkerRef.current.setPosition(pos);
      driverMarkerRef.current.setIcon(icon);
    }
    if (positionRef.current && (corridaStatus === "accepted" || corridaStatus === "arrived")) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(positionRef.current);
      bounds.extend(pos);
      fitBoundsComPainel(bounds);
    } else if (positionRef.current && destino && corridaStatus === "in_progress") {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(positionRef.current);
      bounds.extend(pos);
      bounds.extend({ lat: destino.lat, lng: destino.lng });
      fitBoundsComPainel(bounds);
    }
  }, [mapReady, motoristaPosicao, corridaStatus, destino, fitBoundsComPainel]);

  // Rota do motorista até o passageiro (quando corrida aceita)
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || !window.google) return;

    // Inicializa renderer uma vez
    if (!driverRouteRendererRef.current) {
      driverRouteRendererRef.current = new window.google.maps.DirectionsRenderer({
        suppressMarkers: true,
        polylineOptions: { strokeColor: "#22c55e", strokeWeight: 8, strokeOpacity: 0.95 },
      });
      driverRouteRendererRef.current.setMap(mapInstanceRef.current);
    }

    const deveTraçar = corridaConfirmada &&
      (corridaStatus === "accepted" || corridaStatus === "arrived") &&
      motoristaPosicao?.lat && motoristaPosicao?.lng &&
      positionRef.current?.lat && positionRef.current?.lng;

    if (!deveTraçar) {
      driverRouteRendererRef.current.setDirections({ routes: [] });
      driverRouteAlvoRef.current = null;
      return;
    }

    // Só recalcula se o motorista se moveu mais de ~150m (3 decimais ≈ 111m)
    const chave = `${motoristaPosicao.lat.toFixed(3)},${motoristaPosicao.lng.toFixed(3)}`;
    if (driverRouteAlvoRef.current === chave) return;
    driverRouteAlvoRef.current = chave;

    const svc = new window.google.maps.DirectionsService();
    svc.route({
      origin: { lat: motoristaPosicao.lat, lng: motoristaPosicao.lng },
      destination: positionRef.current,
      travelMode: window.google.maps.TravelMode.DRIVING,
    }, (result, status) => {
      if (status === "OK") {
        driverRouteRendererRef.current.setDirections(result);
        // Ajusta zoom para mostrar motorista + passageiro
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend({ lat: motoristaPosicao.lat, lng: motoristaPosicao.lng });
        bounds.extend(positionRef.current);
        fitBoundsComPainel(bounds);
      }
    });
  }, [mapReady, motoristaPosicao, corridaConfirmada, corridaStatus, fitBoundsComPainel]);

  const updateUserMarker = (pos) => {
    if (!mapInstanceRef.current) return;
    if (!userMarkerRef.current) {
      userMarkerRef.current = new window.google.maps.Marker({
        position: pos, map: mapInstanceRef.current,
        title: "Você",
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(PASSENGER_MARKER_SVG)}`,
          scaledSize: new window.google.maps.Size(52, 68),
          anchor: new window.google.maps.Point(26, 60),
        },
        zIndex: 10,
      });
    } else { userMarkerRef.current.setPosition(pos); }
  };

  const goToMyLocation = useCallback(async () => {
    if (!onRequestLocation) {
      try {
          const newPos = await getCurrentLocation({ requestPermission: true, timeout: 10000 });
          positionRef.current = newPos;
          updateUserMarker(newPos);
          mapInstanceRef.current?.setZoom(17);
          centerUserInVisibleMap(newPos, true);
      } catch (_) { alert("Não foi possível obter sua localização."); }
      return;
    }
    const pos = await onRequestLocation();
    if (pos) {
      positionRef.current = pos;
      updateUserMarker(pos);
      mapInstanceRef.current?.setZoom(17);
      centerUserInVisibleMap(pos, true);
    } else {
      alert("Localização não disponível. Verifique as permissões.");
    }
  }, [onRequestLocation, centerUserInVisibleMap]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="zuno-map-shell" style={{ position: "relative", width: "100%", height: "100%", ...style }}>
      <div ref={mapRef} className="zuno-map-canvas" style={{ width: "100%", height: "100%" }} />

      <div className="zuno-map-overlay" aria-hidden="true" />
      {showControls && <div className="zuno-map-brand" aria-label="Zuno">
        <img src={zunoLogo} alt="" aria-hidden="true" />
      </div>}

      {/* Botão minha localização */}
      {showControls && <button className="zuno-map-location" aria-label="Ir para minha localização" onClick={goToMyLocation} style={{ position: "absolute", top: "max(20px, env(safe-area-inset-top))", right: "18px", width: "54px", height: "54px", borderRadius: "18px", border: "1px solid #25314d", background: "rgba(13,20,40,.92)", boxShadow: "0 12px 30px rgba(0,0,0,.34)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="8" stroke="#18D7E5" strokeWidth="2"/>
          <circle cx="12" cy="12" r="3" fill="#7657FF"/>
          <line x1="12" y1="4" x2="12" y2="2" stroke="#4285F4" strokeWidth="2"/>
          <line x1="12" y1="22" x2="12" y2="20" stroke="#4285F4" strokeWidth="2"/>
          <line x1="4" y1="12" x2="2" y2="12" stroke="#4285F4" strokeWidth="2"/>
          <line x1="22" y1="12" x2="20" y2="12" stroke="#4285F4" strokeWidth="2"/>
        </svg>
      </button>}

      {!mapReady && (
        <div className="zuno-map-loading" role="status" style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 50% 40%,#122b47,#070b18 65%)", display: "flex", flexDirection: "column", gap: 14, alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          {!mapError && <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid #e5e7eb", borderTopColor: "#4285F4", animation: "spin 0.8s linear infinite" }} />}
          {mapError && <>
            <strong style={{ color: "#f7fbff" }}>O mapa não pôde ser carregado.</strong>
            <span style={{ color: "#b9c9df", fontSize: 13 }}>Você ainda pode informar seu endereço manualmente.</span>
            <button type="button" onClick={() => window.location.reload()} style={{ minHeight: 42, padding: "0 18px", border: "1px solid #18d7e5", borderRadius: 12, background: "rgba(24,215,229,.12)", color: "#eafcff", fontWeight: 700 }}>Tentar novamente</button>
          </>}
        </div>
      )}
      <MapDiagnosticPanel />
    </div>
  );
}

// ── Estilo local do Google Maps — identidade dark Zuno ──
export const ZUNO_MAP_STYLES = [
  // Fundo navy e tipografia de baixo ruído
  { elementType: "geometry", stylers: [{ color: "#081326" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8796AD" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#071224" }] },

  // Estradas
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#202D46" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#111D31" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#AAB5C7" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#304462" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#16CFF4" }, { weight: 0.7 }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#D2D9E5" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#26344D" }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#202D46" }] },

  // Água bem azul
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#07334F" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#20C8ED" }] },

  // Natureza e parques
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#091729" }] },
  { featureType: "landscape.man_made", elementType: "geometry", stylers: [{ color: "#0b1629" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#102338" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#58768c" }] },

  // POIs secundários ocultos
  { featureType: "poi", elementType: "all", stylers: [{ visibility: "off" }] },
  { featureType: "poi.medical", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "poi.food_and_drink", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "poi.school", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "poi.store", elementType: "labels.icon", stylers: [{ visibility: "off" }] },

  // Trânsito oculto
  { featureType: "transit", stylers: [{ visibility: "off" }] },

  // Bairros e localidades
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#17243A" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#D2D9E5" }] },
  { featureType: "administrative.neighborhood", elementType: "labels.text.fill", stylers: [{ color: "#8796AD" }] },

];

// Paleta clara controlada: preserva o mapa como parte da identidade Zuno sem filtros CSS.
export const ZUNO_LIGHT_MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#EEF2F7" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#536579" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#F8FAFC" }, { weight: 3 }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#FFFFFF" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#DCE4ED" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#43556B" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#DDEBF4" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#9CCCE0" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#263B52" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#F8FAFC" }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#FFFFFF" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#CDE8F3" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#39758E" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#EAF1EC" }] },
  { featureType: "landscape.man_made", elementType: "geometry", stylers: [{ color: "#F1F4F8" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#DDEDE3" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#587565" }] },
  { featureType: "poi", elementType: "all", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#CDD7E3" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#18324C" }] },
  { featureType: "administrative.neighborhood", elementType: "labels.text.fill", stylers: [{ color: "#65768A" }] },
];

export function getZunoMapStyles(theme) {
  return theme === "light" ? ZUNO_LIGHT_MAP_STYLES : ZUNO_MAP_STYLES;
}

export function calculateMapPadding(mapRect, sheetRect, desktop = false) {
  const base = { top: 86, right: 44, bottom: 64, left: 44 };
  if (!sheetRect) return base;
  if (desktop) {
    return {
      ...base,
      left: Math.min(mapRect.width * .58, Math.max(base.left, sheetRect.right - mapRect.left + 24)),
    };
  }
  const coveredHeight = Math.max(0, mapRect.bottom - sheetRect.top);
  return {
    ...base,
    bottom: Math.min(mapRect.height * .68, Math.max(base.bottom, coveredHeight + 24)),
  };
}
